import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { userApi } from '../services/api';

interface UsageStats {
  weekly_cost_spent: number;
  weekly_cost_limit: number | null;
  weekly_usage_percentage: number;
  week_resets_at: string;
  has_weekly_limit: boolean;
  subscription_status: string | null;
  monthly_usage: number;
  monthly_limit: number | null;
  tier_name: string | null;
}

const UsageIndicator: React.FC = () => {
  const { data: stats, isLoading, isError } = useQuery({
    queryKey: ['userStats'],
    queryFn: () => userApi.getStats().then((r) => r.data as UsageStats),
    refetchInterval: 60000, // Refresh every minute
    retry: 2,
  });

  // Show loading state briefly
  if (isLoading) {
    return (
      <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg w-32">
        <div className="animate-pulse h-2 bg-gray-200 dark:bg-gray-700 rounded"></div>
      </div>
    );
  }

  // If error or no stats, show minimal indicator
  if (isError || !stats) {
    return (
      <Link to="/subscription" className="px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg text-xs text-gray-500">
        View Plan
      </Link>
    );
  }

  // Determine what to show: weekly limit or monthly usage
  const hasWeeklyLimit = stats.has_weekly_limit && stats.weekly_cost_limit;
  const hasMonthlyLimit = stats.monthly_limit && stats.monthly_limit > 0;

  let percentage: number;
  let resetText: string;

  if (hasWeeklyLimit) {
    percentage = stats.weekly_usage_percentage;
    const resetsAt = new Date(stats.week_resets_at);
    const now = new Date();
    const daysUntilReset = Math.ceil((resetsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    resetText = daysUntilReset === 0 ? 'Resets today' : daysUntilReset === 1 ? '1 day left' : `${daysUntilReset} days left`;
  } else if (hasMonthlyLimit) {
    percentage = (stats.monthly_usage / stats.monthly_limit!) * 100;
    // Calculate days until end of month
    const now = new Date();
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const daysUntilReset = Math.ceil((endOfMonth.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    resetText = `${stats.monthly_usage}/${stats.monthly_limit} this month`;
  } else {
    // No limits - show tier name only
    return (
      <Link to="/subscription" className="px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {stats.tier_name || 'Free'} Plan
        </span>
      </Link>
    );
  }

  // Determine color based on percentage
  const getBarColor = () => {
    if (percentage >= 100) return 'bg-red-500';
    if (percentage >= 80) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getTextColor = () => {
    if (percentage >= 100) return 'text-red-600 dark:text-red-400';
    if (percentage >= 80) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-600 dark:text-green-400';
  };

  return (
    <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-gray-500 dark:text-gray-400">Usage</span>
        <Link
          to="/subscription"
          className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400"
        >
          {percentage >= 80 ? 'Upgrade' : 'Manage'}
        </Link>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full ${getBarColor()} transition-all duration-300`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>

      {/* Stats - only show percentage and reset info */}
      <div className="flex items-center justify-between mt-1">
        <span className={`text-xs font-medium ${getTextColor()}`}>
          {Math.round(percentage)}% used
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {resetText}
        </span>
      </div>

      {/* Warning message if over 90% */}
      {percentage >= 90 && percentage < 100 && (
        <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
          Approaching limit. Consider upgrading.
        </p>
      )}

      {/* Limit reached message */}
      {percentage >= 100 && (
        <p className="text-xs text-red-600 dark:text-red-400 mt-1">
          Limit reached. {resetText}
        </p>
      )}
    </div>
  );
};

export default UsageIndicator;
